import { useState, useMemo, useRef, useEffect, memo } from "react";
import { useMutation, useQuery, useQueries, useQueryClient } from "@tanstack/react-query";
import { UtensilsCrossed, Search, X, Plus, Trash2, ChefHat, AlertTriangle, Layers, ArrowRightLeft, Pencil, Save, Loader2, ChevronDown } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import {
  fetchProcessedInvoices, fetchIngredients, fetchIngredientHistory,
  fetchRecipes, createRecipe, updateRecipe, deleteRecipe,
  type Ingredient as CatalogIngredient,
  type Recipe, type RecipePayload, type RecipeComponent,
} from "../../services/apiService";
import { Invoice } from "../../types";
import { useBusiness } from "../../contexts/BusinessContext";
import BaseIngredientTab from "./BaseIngredientTab";

const NAVY    = "#2a2420";
const ORANGE  = "#607840";
const SURFACE = "#fff";
const CANVAS  = "#fff";
const LINE    = "#e9e1d4";
const MUTED   = "#8a8074";
const ACCENT_WEAK   = "rgba(96,120,64,0.12)";
const ACCENT_LINE   = "rgba(96,120,64,0.30)";
const ACCENT_SHADOW = "0 2px 8px rgba(96,120,64,0.25)";
const UP      = "#c2412e";
const UP_WEAK = "rgba(194,65,46,0.12)";
const DOWN    = "#3c7d57";
const TAB_ACCENT = "#ff6f61";

// ── Unit system ───────────────────────────────────────────────────────────────

const UNITS: Record<string, { base: string; factor: number }> = {
  "g":       { base: "mass",    factor: 1 },
  "kg":      { base: "mass",    factor: 1000 },
  "oz":      { base: "mass",    factor: 28.35 },
  "lb":      { base: "mass",    factor: 453.59 },
  "mL":      { base: "volume",  factor: 1 },
  "L":       { base: "volume",  factor: 1000 },
  "tsp":     { base: "volume",  factor: 4.929 },
  "tbsp":    { base: "volume",  factor: 14.787 },
  "cup":     { base: "volume",  factor: 236.588 },
  "fl oz":   { base: "volume",  factor: 29.574 },
  "ea":      { base: "ea",      factor: 1 },
  "piece":   { base: "piece",   factor: 1 },
  "portion": { base: "portion", factor: 1 },
  "serving": { base: "serving", factor: 1 },
  "batch":   { base: "batch",   factor: 1 },
};
const UNIT_LIST = Object.keys(UNITS);
const UNIT_GROUPS = [
  { label: "Mass",   units: ["g", "kg", "oz", "lb"] },
  { label: "Volume", units: ["mL", "L", "tsp", "tbsp", "cup", "fl oz"] },
  { label: "Count",  units: ["ea", "piece", "portion", "serving", "batch"] },
];

function compatibleUnits(src: string): string[] {
  const def = UNITS[src];
  if (!def) return [src];
  return UNIT_LIST.filter(u => UNITS[u]?.base === def.base);
}
function convertUnit(cost: number, from: string, to: string): number | null {
  if (from === to) return cost;
  const f = UNITS[from]; const t = UNITS[to];
  if (!f || !t || f.base !== t.base) return null;
  return cost * (t.factor / f.factor);
}
function usageCost(qty: number, usageUnit: string, costPerSrc: number, srcUnit: string): number | null {
  const rate = convertUnit(costPerSrc, srcUnit, usageUnit);
  return rate === null ? null : qty * rate;
}

// ── AllProduct ────────────────────────────────────────────────────────────────

interface AllProduct {
  productCode: string;
  description: string;
  supplierId: string;
  supplierName: string;
  unit: string;
  latestUnitPrice: number;
  latestSeenAt: string;
  totalSpend: number;
  timesOrdered: number;
  isIngredient?: boolean;
  sourceCount?: number;
}

// ── Cost calculation ──────────────────────────────────────────────────────────

interface CostResult { value: number | null; warning: string | null; }

function getRecipeCostPerUnit(
  recipe: Recipe,
  allRecipes: Recipe[],
  allProducts: AllProduct[],
  visited = new Set<string>(),
): CostResult {
  if (visited.has(recipe.id)) return { value: null, warning: "Circular reference" };
  visited.add(recipe.id);
  if (recipe.yieldQty <= 0) return { value: null, warning: "Yield must be > 0" };
  if (recipe.components.length === 0) return { value: 0, warning: null };

  let total = 0;
  for (const comp of recipe.components) {
    if (comp.refType === "ingredient") {
      const prod = allProducts.find(p => p.productCode === comp.refId);
      if (!prod) return { value: null, warning: `"${comp.name}" not in catalogue` };
      const cost = usageCost(comp.quantity, comp.unit, prod.latestUnitPrice, prod.unit);
      if (cost === null) return { value: null, warning: `Incompatible units: ${comp.unit} ↔ ${prod.unit}` };
      total += cost;
    } else {
      const sub = allRecipes.find(r => r.id === comp.refId);
      if (!sub) return { value: null, warning: `Recipe "${comp.name}" was deleted` };
      const subResult = getRecipeCostPerUnit(sub, allRecipes, allProducts, new Set(visited));
      if (subResult.value === null) return { value: null, warning: subResult.warning };
      const cost = usageCost(comp.quantity, comp.unit, subResult.value, sub.yieldUnit);
      if (cost === null) return { value: null, warning: `Incompatible units: ${comp.unit} ↔ ${sub.yieldUnit}` };
      total += cost;
    }
  }
  return { value: total / recipe.yieldQty, warning: null };
}

function wouldCreateCycle(targetId: string, newSubId: string, allRecipes: Recipe[]): boolean {
  function canReach(from: string, to: string, seen: Set<string>): boolean {
    if (from === to) return true;
    if (seen.has(from)) return false;
    seen.add(from);
    const r = allRecipes.find(x => x.id === from);
    return r?.components.some(c => c.refType === "recipe" && canReach(c.refId, to, seen)) ?? false;
  }
  return canReach(newSubId, targetId, new Set());
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const DEFAULT_FC_TARGET = 30;

function fcColor(pct: number | null, target = DEFAULT_FC_TARGET): string {
  if (pct === null) return NAVY;
  if (pct <= target) return DOWN;
  if (pct <= target + 8) return ORANGE;
  return UP;
}
function fmtCost(n: number | null): string {
  return n === null ? "—" : `$${n.toFixed(2)}`;
}
function invoiceActivityDate(inv: Invoice): string {
  return inv.invoiceDate || inv.uploadedAt || inv.receivedDate || "";
}
function recipeToPayload(r: Recipe): RecipePayload {
  return {
    name: r.name, recipeType: r.recipeType, category: r.category,
    yieldQty: r.yieldQty, yieldUnit: r.yieldUnit,
    sellingPrice: r.sellingPrice, targetFoodCostPct: r.targetFoodCostPct,
    notes: r.notes,
    components: r.components.map(c => ({
      refId: c.refId, refType: c.refType, name: c.name,
      quantity: c.quantity, unit: c.unit, sourceUnit: c.sourceUnit,
    })),
  };
}

// ── UnitSelect ────────────────────────────────────────────────────────────────

function UnitSelect({ value, sourceUnit, onChange }: {
  value: string; sourceUnit: string; onChange: (u: string) => void;
}) {
  const known = Boolean(UNITS[sourceUnit]);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => { if (!known && value !== sourceUnit) onChangeRef.current(sourceUnit); }, [known, value, sourceUnit]);

  if (!known) return (
    <span className="inline-block rounded-md px-2 py-1 text-xs font-medium"
      style={{ color: NAVY, backgroundColor: `${NAVY}08`, border: `1.5px solid ${NAVY}14` }}>{sourceUnit}</span>
  );
  const allowed = compatibleUnits(sourceUnit);
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      className="rounded-md px-1.5 py-1 text-xs outline-none"
      style={{ border: `1.5px solid ${NAVY}20`, color: NAVY, backgroundColor: SURFACE }}>
      {UNIT_GROUPS.map(g => {
        const vis = g.units.filter(u => allowed.includes(u));
        if (!vis.length) return null;
        return <optgroup key={g.label} label={g.label}>{vis.map(u => <option key={u} value={u}>{u}</option>)}</optgroup>;
      })}
    </select>
  );
}

// ── GroupCard ─────────────────────────────────────────────────────────────────

function GroupCard({ name, count, onClick }: { name: string; count: number; onClick: () => void; }) {
  const [hovered, setHovered] = useState(false);
  const isAll = name === "All";
  const isUngrouped = name === "Ungrouped";
  return (
    <div className="rounded-2xl transition-shadow cursor-pointer"
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ border: `1.5px solid ${NAVY}0e`, backgroundColor: isAll ? `${NAVY}04` : "white",
        boxShadow: hovered ? "0 4px 20px rgba(80,55,30,0.09)" : "none" }}>
      <button data-menu-costing-card type="button" onClick={onClick} className="w-full text-left p-5">
        <div className="flex items-start justify-between mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: isAll ? `${NAVY}12` : `${NAVY}08` }}>
            {isAll ? <Layers size={18} style={{ color: NAVY, opacity: 0.6 }} />
              : <ChefHat size={18} style={{ color: NAVY, opacity: 0.4 }} />}
          </div>
          <span className="text-xs font-bold rounded-full px-2.5 py-1"
            style={{ backgroundColor: `${NAVY}09`, color: NAVY }}>{count}</span>
        </div>
        <p className="text-sm font-extrabold truncate" style={{ color: NAVY, opacity: isUngrouped ? 0.45 : 1 }}>{name}</p>
        <p className="text-[11px] mt-0.5" style={{ color: NAVY, opacity: 0.4 }}>{count} item{count !== 1 ? "s" : ""}</p>
      </button>
    </div>
  );
}

// ── ComponentSearch ───────────────────────────────────────────────────────────

type CompSearchResult =
  | { kind: "ingredient"; id: string; name: string; unit: string }
  | { kind: "recipe"; id: string; name: string; yieldUnit: string };

function ComponentSearch({ catalogIngredients, subRecipes, existingRefs, onSelect, onCancel }: {
  catalogIngredients: CatalogIngredient[];
  subRecipes: Recipe[];
  existingRefs: Set<string>;
  onSelect: (r: CompSearchResult) => void;
  onCancel: () => void;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    function onOut(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) onCancel();
    }
    document.addEventListener("mousedown", onOut);
    return () => document.removeEventListener("mousedown", onOut);
  }, [onCancel]);

  const results = useMemo<(CompSearchResult & { label: string; sub: string })[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const out: (CompSearchResult & { label: string; sub: string })[] = [];
    for (const ing of catalogIngredients) {
      if (existingRefs.has(ing.id) || !ing.name.toLowerCase().includes(q)) continue;
      out.push({ kind: "ingredient", id: ing.id, name: ing.name, unit: ing.canonicalUnit,
        label: ing.name,
        sub: `Ingredient · ${ing.canonicalUnit}${ing.currentPrice != null ? ` · $${ing.currentPrice.toFixed(2)}/${ing.canonicalUnit}` : ""}`,
      });
    }
    for (const r of subRecipes) {
      if (existingRefs.has(r.id) || !r.name.toLowerCase().includes(q)) continue;
      out.push({ kind: "recipe", id: r.id, name: r.name, yieldUnit: r.yieldUnit,
        label: r.name, sub: `Prep · yields ${r.yieldQty} ${r.yieldUnit}` });
    }
    return out.slice(0, 10);
  }, [query, catalogIngredients, subRecipes, existingRefs]);

  return (
    <div ref={containerRef} className="relative">
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg"
        style={{ border: `1.5px solid ${NAVY}`, backgroundColor: SURFACE }}>
        <Search size={12} style={{ color: NAVY, opacity: 0.5, flexShrink: 0 }} />
        <input ref={inputRef} value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search ingredients or preps…"
          className="flex-1 text-xs outline-none" style={{ color: NAVY }}
          onKeyDown={e => e.key === "Escape" && onCancel()} />
        <button type="button" onClick={onCancel} style={{ color: NAVY, opacity: 0.4 }}><X size={12} /></button>
      </div>
      {open && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 rounded-lg z-20 shadow-lg overflow-y-auto max-h-60"
          style={{ border: `1px solid ${NAVY}18`, backgroundColor: SURFACE }}>
          {results.map((r, i) => (
            <button key={r.id} type="button" onClick={() => onSelect(r)}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-left"
              style={{ borderTop: i > 0 ? `1px solid ${NAVY}0d` : undefined, backgroundColor: SURFACE }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = `${NAVY}06`)}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = SURFACE)}>
              <span className="flex-shrink-0 text-[9px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5"
                style={{ backgroundColor: r.kind === "ingredient" ? `${ORANGE}15` : `${NAVY}10`,
                  color: r.kind === "ingredient" ? ORANGE : NAVY }}>
                {r.kind === "ingredient" ? "Ingredient" : "Prep"}
              </span>
              <div className="min-w-0">
                <p className="text-xs font-semibold truncate" style={{ color: NAVY }}>{r.label}</p>
                <p className="text-[10px] truncate" style={{ color: NAVY, opacity: 0.5 }}>{r.sub}</p>
              </div>
            </button>
          ))}
        </div>
      )}
      {open && results.length === 0 && query.trim() && (
        <div className="absolute top-full left-0 right-0 mt-1 rounded-lg z-20 px-3 py-3"
          style={{ border: `1px solid ${NAVY}18`, backgroundColor: SURFACE }}>
          <p className="text-xs" style={{ color: NAVY, opacity: 0.45 }}>No results for "{query}"</p>
        </div>
      )}
    </div>
  );
}

// ── RecipePriceHistory ────────────────────────────────────────────────────────

function collectLeafIngredientIds(recipe: Recipe, allRecipes: Recipe[], visited = new Set<string>()): Set<string> {
  const ids = new Set<string>();
  for (const comp of recipe.components) {
    if (comp.refType === "ingredient") {
      ids.add(comp.refId);
    } else {
      const sub = allRecipes.find(r => r.id === comp.refId);
      if (sub && !visited.has(sub.id)) {
        visited.add(sub.id);
        for (const id of collectLeafIngredientIds(sub, allRecipes, visited)) ids.add(id);
      }
    }
  }
  return ids;
}

function RecipePriceHistory({ recipe, allRecipes, allProducts, businessId }: {
  recipe: Recipe; allRecipes: Recipe[]; allProducts: AllProduct[]; businessId: string | undefined;
}) {
  const ingredientIds = useMemo(
    () => [...collectLeafIngredientIds(recipe, allRecipes)],
    [recipe, allRecipes],
  );

  const histories = useQueries({
    queries: ingredientIds.map(id => ({
      queryKey: ["ingredient-history", id, businessId],
      queryFn: () => fetchIngredientHistory(id, businessId),
      enabled: Boolean(businessId),
    })),
  });

  const series = useMemo(() => {
    const priceMap = new Map<string, { date: string; price: number; unit: string }[]>();
    ingredientIds.forEach((id, i) => {
      const prod = allProducts.find(p => p.productCode === id);
      const unit = prod?.unit ?? "ea";
      const entries = (histories[i]?.data ?? []).map(pt => ({ date: pt.date, price: pt.price, unit }));
      if (entries.length) priceMap.set(id, entries);
    });
    for (const arr of priceMap.values()) arr.sort((a, b) => a.date.localeCompare(b.date));

    const allDates = [...new Set([...priceMap.values()].flatMap(a => a.map(p => p.date)))].sort();
    const currentPrices = new Map<string, { price: number; unit: string }>();
    const out: { date: string; cost: number }[] = [];

    for (const date of allDates) {
      for (const [id, arr] of priceMap) {
        const pts = arr.filter(p => p.date <= date);
        if (pts.length) currentPrices.set(id, pts[pts.length - 1]);
      }
      const snapshot = allProducts.map(p => {
        const h = currentPrices.get(p.productCode);
        return h ? { ...p, latestUnitPrice: h.price, unit: h.unit } : p;
      });
      const r = getRecipeCostPerUnit(recipe, allRecipes, snapshot);
      if (r.value !== null) {
        const totalCost = r.value * recipe.yieldQty;
        out.push({ date, cost: totalCost });
      }
    }
    return out;
  }, [ingredientIds, histories, allProducts, recipe, allRecipes]);

  if (histories.some(q => q.isLoading)) return <p className="text-xs italic" style={{ color: MUTED }}>Loading…</p>;
  if (series.length === 0) return (
    <p className="text-xs italic" style={{ color: MUTED }}>
      No history yet — appears once ingredients are linked and invoices arrive.
    </p>
  );
  const costs = series.map((point) => point.cost).filter(Number.isFinite);
  const minCost = costs.length ? Math.min(...costs) : 0;
  const maxCost = costs.length ? Math.max(...costs) : 1;
  const costRange = maxCost - minCost;
  const yAxisPad = costRange > 0 ? Math.max(costRange * 0.18, maxCost * 0.015) : Math.max(maxCost * 0.05, 0.05);
  const yAxisMin = Math.max(0, minCost - yAxisPad);
  const yAxisMax = maxCost + yAxisPad;

  return (
    <ResponsiveContainer width="100%" height={160}>
      <LineChart data={series} margin={{ top: 6, right: 12, bottom: 0, left: -10 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={LINE} vertical={false} />
        <XAxis dataKey="date" tick={{ fontSize: 9, fill: MUTED }} tickFormatter={d => String(d).slice(5)} />
        <YAxis
          tick={{ fontSize: 9, fill: MUTED }}
          width={44}
          domain={[yAxisMin, yAxisMax]}
          tickFormatter={v => `$${Number(v).toFixed(2)}`}
        />
        <Tooltip formatter={(v: number) => [`$${Number(v).toFixed(2)}`, "Total cost"]}
          labelFormatter={l => String(l)}
          contentStyle={{ fontSize: 11, borderRadius: 8, border: `1px solid ${LINE}` }} />
        <Line type="monotone" dataKey="cost" stroke={ORANGE} strokeWidth={1.5}
          isAnimationActive={false} dot={{ r: 4, fill: ORANGE, stroke: "#fff", strokeWidth: 1 }} />
      </LineChart>
    </ResponsiveContainer>
  );
}

// ── RecipeEditor ──────────────────────────────────────────────────────────────

function RecipeEditor({ recipe, allRecipes, allProducts, catalogIngredients, categories, businessId, onUpdate, onDelete, onConvert, isDraft, saveDraftPending, onSaveDraft, onCancelDraft }: {
  recipe: Recipe;
  allRecipes: Recipe[];
  allProducts: AllProduct[];
  catalogIngredients: CatalogIngredient[];
  categories: string[];
  businessId: string | undefined;
  onUpdate: (r: Recipe) => void;
  onDelete: () => void;
  onConvert: (r: Recipe) => void;
  isDraft?: boolean;
  saveDraftPending?: boolean;
  onSaveDraft?: (r: Recipe) => void;
  onCancelDraft?: () => void;
}) {
  const [local, setLocal] = useState<Recipe>(recipe);
  const [adding, setAdding] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Reset local state when a different recipe is selected
  useEffect(() => { setLocal(recipe); }, [recipe.id]); // intentional: reset local only on recipe change, not every field update
  useEffect(() => { if (!recipe.name) nameRef.current?.focus(); }, [recipe.id]); // intentional: focus only on recipe switch
  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const isMenuItem = local.recipeType === "menu_item";
  const costPerUnit = getRecipeCostPerUnit(local, allRecipes, allProducts);
  const totalCost = costPerUnit.value !== null ? costPerUnit.value * local.yieldQty : null;
  const sellingPrice = local.sellingPrice ?? 0;
  const targetFc = local.targetFoodCostPct > 0 ? local.targetFoodCostPct : DEFAULT_FC_TARGET;
  const fcPct = totalCost !== null && sellingPrice > 0 ? (totalCost / sellingPrice) * 100 : null;
  const fcCol = fcColor(fcPct, targetFc);

  const excludeIds = useMemo(() => {
    const s = new Set<string>([local.id]);
    for (const r of allRecipes) { if (wouldCreateCycle(local.id, r.id, allRecipes)) s.add(r.id); }
    return s;
  }, [local.id, allRecipes]);

  const existingRefs = useMemo(() => new Set(local.components.map(c => c.refId)), [local.components]);

  function set<K extends keyof Recipe>(k: K, v: Recipe[K]) {
    const updated = { ...local, [k]: v };
    setLocal(updated);
    if (isDraft) {
      onUpdate(updated);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onUpdate(updated), 500);
  }

  function addComponent(r: CompSearchResult) {
    const comp: RecipeComponent = r.kind === "ingredient"
      ? { id: crypto.randomUUID(), recipeId: local.id, refId: r.id, refType: "ingredient",
          name: r.name, quantity: 1, unit: r.unit, sourceUnit: r.unit, sortOrder: local.components.length }
      : { id: crypto.randomUUID(), recipeId: local.id, refId: r.id, refType: "recipe",
          name: r.name, quantity: 1, unit: r.yieldUnit, sourceUnit: r.yieldUnit, sortOrder: local.components.length };
    const updated = { ...local, components: [...local.components, comp] };
    setLocal(updated);
    onUpdate(updated);
    setAdding(false);
  }

  function updateComp(refId: string, patch: Partial<RecipeComponent>) {
    const updated = { ...local, components: local.components.map(c => c.refId === refId ? { ...c, ...patch } : c) };
    setLocal(updated);
    if (isDraft) {
      onUpdate(updated);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onUpdate(updated), 500);
  }

  function removeComp(refId: string) {
    const updated = { ...local, components: local.components.filter(c => c.refId !== refId) };
    setLocal(updated);
    onUpdate(updated);
  }

  const availableSubRecipes = allRecipes.filter(r =>
    r.recipeType === "sub_recipe" && !excludeIds.has(r.id)
  );

  function saveDraft() {
    if (!onSaveDraft) return;
    clearTimeout(debounceRef.current);
    onSaveDraft({
      ...local,
      name: local.name.trim() || (isMenuItem ? "New menu item" : "New recipe"),
      yieldQty: local.yieldQty > 0 ? local.yieldQty : 1,
      yieldUnit: local.yieldUnit || (isMenuItem ? "serving" : "ea"),
      targetFoodCostPct: local.targetFoodCostPct > 0 ? local.targetFoodCostPct : DEFAULT_FC_TARGET,
    });
  }

  return (
    <div className="p-6">
      {/* Name + delete */}
      <div className="flex items-start gap-3 mb-5">
        <input ref={nameRef} value={local.name} onChange={e => set("name", e.target.value)}
          placeholder={isMenuItem ? "Menu item name…" : "Sub recipe name…"}
          className="flex-1 text-xl font-extrabold outline-none bg-transparent pb-1"
          style={{ color: NAVY, borderBottom: `2px solid ${NAVY}18` }}
          onFocus={e => (e.target.style.borderBottomColor = NAVY)}
          onBlur={e => (e.target.style.borderBottomColor = `${NAVY}18`)} />
        {isDraft ? (
          <>
            <button data-menu-costing-action type="button" disabled={saveDraftPending} onClick={saveDraft}
              className="rounded-lg px-4 py-2 flex-shrink-0"
              style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#fff", backgroundColor: MC_ACCENT, border: "none", fontSize: 13, fontWeight: 700, cursor: saveDraftPending ? "not-allowed" : "pointer", opacity: saveDraftPending ? 0.65 : 1 }}>
              {saveDraftPending ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              Save
            </button>
            <button data-menu-costing-action type="button" onClick={onCancelDraft}
              className="rounded-lg p-2 flex-shrink-0"
              style={{ color: MUTED, backgroundColor: `${NAVY}09`, border: "none", cursor: "pointer" }}>
              <X size={14} />
            </button>
          </>
        ) : (
          <button type="button" onClick={() => setConfirmDelete(v => !v)}
            className="rounded-lg p-2 flex-shrink-0" style={{ color: UP, backgroundColor: UP_WEAK }}>
            <Trash2 size={14} />
          </button>
        )}
      </div>

      {confirmDelete && (
        <div className="mb-5 flex items-center gap-3 rounded-lg px-4 py-3"
          style={{ backgroundColor: UP_WEAK, border: "1px solid rgba(220,38,38,0.2)" }}>
          <p className="text-xs flex-1" style={{ color: "#b91c1c" }}>Delete "{local.name || "this recipe"}"?</p>
          <button type="button" onClick={onDelete}
            className="text-xs font-bold rounded-md px-3 py-1.5 text-white flex-shrink-0"
            style={{ backgroundColor: UP }}>Delete</button>
          <button type="button" onClick={() => setConfirmDelete(false)}
            className="text-xs font-bold rounded-md px-3 py-1.5 flex-shrink-0"
            style={{ color: NAVY, backgroundColor: `${NAVY}0d` }}>Cancel</button>
        </div>
      )}

      {/* Convert type */}
      {!isDraft && <div className="mb-5">
        <button type="button"
          onClick={() => onConvert({ ...local, recipeType: isMenuItem ? "sub_recipe" : "menu_item",
            sellingPrice: isMenuItem ? null : 0 })}
          className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3 py-1.5"
          style={{ color: NAVY, opacity: 0.55, backgroundColor: `${NAVY}07`, border: `1px solid ${NAVY}12` }}>
          <ArrowRightLeft size={11} />
          Convert to {isMenuItem ? "Prep" : "Menu Item"}
        </button>
      </div>}

      {/* Category + Yield */}
      <div className="flex items-end gap-4 mb-5 flex-wrap">
        <div>
          <label className="text-[10px] uppercase tracking-wide font-semibold block mb-1.5"
            style={{ color: NAVY, opacity: 0.45 }}>Category</label>
          <select value={local.category ?? ""} onChange={e => set("category", e.target.value || null)}
            className="rounded-lg px-3 py-2 text-sm outline-none"
            style={{ border: `1.5px solid ${NAVY}24`, color: NAVY, backgroundColor: SURFACE, minWidth: "160px" }}>
            <option value="">— None —</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wide font-semibold block mb-1.5"
            style={{ color: NAVY, opacity: 0.45 }}>{isMenuItem ? "Serves" : "Makes"}</label>
          <div className="flex items-center gap-2">
            <input type="number" min="0.001" step="0.001" value={local.yieldQty || ""}
              onChange={e => set("yieldQty", parseFloat(e.target.value) || 0)}
              className="w-20 rounded-lg px-2 py-2 text-sm font-semibold outline-none"
              style={{ border: `1.5px solid ${NAVY}24`, color: NAVY }} />
            <select value={local.yieldUnit} onChange={e => set("yieldUnit", e.target.value)}
              className="rounded-lg px-2 py-2 text-sm font-semibold outline-none"
              style={{ border: `1.5px solid ${NAVY}24`, color: NAVY, backgroundColor: SURFACE }}>
              {UNIT_GROUPS.map(g => (
                <optgroup key={g.label} label={g.label}>
                  {g.units.map(u => <option key={u} value={u}>{u}</option>)}
                </optgroup>
              ))}
            </select>
          </div>
        </div>

        {isMenuItem && (
          <>
            <div>
              <label className="text-[10px] uppercase tracking-wide font-semibold block mb-1.5"
                style={{ color: NAVY, opacity: 0.45 }}>Selling Price</label>
              <div className="flex items-center rounded-lg overflow-hidden" style={{ border: `1.5px solid ${NAVY}24` }}>
                <span className="px-2.5 py-2 text-xs font-bold"
                  style={{ color: NAVY, opacity: 0.4, backgroundColor: `${NAVY}05` }}>$</span>
                <input type="number" min="0" step="0.01" value={local.sellingPrice ?? ""}
                  onChange={e => set("sellingPrice", parseFloat(e.target.value) || 0)}
                  placeholder="0.00" className="w-24 px-2 py-2 text-sm font-semibold outline-none"
                  style={{ color: NAVY, backgroundColor: SURFACE }} />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide font-semibold block mb-1.5"
                style={{ color: NAVY, opacity: 0.45 }}>Target FC</label>
              <div className="flex items-center rounded-lg overflow-hidden" style={{ border: `1.5px solid ${NAVY}24` }}>
                <input type="number" min="1" max="100" step="0.1" value={local.targetFoodCostPct}
                  onChange={e => set("targetFoodCostPct", parseFloat(e.target.value) || DEFAULT_FC_TARGET)}
                  className="w-16 px-2 py-2 text-sm font-semibold outline-none"
                  style={{ color: NAVY, backgroundColor: SURFACE }} />
                <span className="px-2 py-2 text-xs font-bold"
                  style={{ color: NAVY, opacity: 0.4, backgroundColor: `${NAVY}05` }}>%</span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Cost summary */}
      {costPerUnit.warning ? (
        <div className="mb-5 flex items-center gap-2 rounded-xl px-4 py-3"
          style={{ backgroundColor: "rgba(217,119,6,0.06)", border: "1px solid rgba(217,119,6,0.2)" }}>
          <AlertTriangle size={14} style={{ color: "#d97706", flexShrink: 0 }} />
          <p className="text-xs" style={{ color: "#92400e" }}>{costPerUnit.warning}</p>
        </div>
      ) : isMenuItem ? (
        <div className="grid grid-cols-3 gap-4 mb-5 rounded-xl p-4"
          style={{ backgroundColor: `${NAVY}04`, border: `1px solid ${NAVY}0d` }}>
          <div>
            <p className="text-[10px] uppercase tracking-wide font-semibold mb-1" style={{ color: NAVY, opacity: 0.45 }}>Total Cost</p>
            <p className="text-xl font-extrabold" style={{ color: NAVY }}>{fmtCost(totalCost)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wide font-semibold mb-1" style={{ color: NAVY, opacity: 0.45 }}>Food Cost %</p>
            <p className="text-xl font-extrabold" style={{ color: fcCol }}>{fcPct !== null ? `${fcPct.toFixed(1)}%` : "—"}</p>
            {fcPct !== null && (
              <p className="text-[10px] mt-0.5" style={{ color: fcCol, opacity: 0.85 }}>
                {fcPct <= targetFc ? "On target" : fcPct <= targetFc + 8 ? "Above target" : "Too high"}
              </p>
            )}
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wide font-semibold mb-1" style={{ color: NAVY, opacity: 0.45 }}>Gross Profit</p>
            <p className="text-xl font-extrabold"
              style={{ color: totalCost !== null && sellingPrice - totalCost > 0 ? DOWN : NAVY }}>
              {totalCost !== null && sellingPrice > 0 ? `$${(sellingPrice - totalCost).toFixed(2)}` : "—"}
            </p>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-6 mb-5 rounded-xl px-4 py-3"
          style={{ backgroundColor: `${NAVY}04`, border: `1px solid ${NAVY}0d` }}>
          <div>
            <p className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: NAVY, opacity: 0.45 }}>Batch cost</p>
            <p className="text-base font-extrabold" style={{ color: NAVY }}>{fmtCost(totalCost)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: NAVY, opacity: 0.45 }}>
              Cost per {local.yieldUnit}
            </p>
            <p className="text-base font-extrabold" style={{ color: ORANGE }}>{fmtCost(costPerUnit.value)}</p>
          </div>
        </div>
      )}

      {/* Components */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-bold uppercase tracking-widest" style={{ color: NAVY, opacity: 0.55 }}>
          Components ({local.components.length})
        </p>
        <button type="button" onClick={() => setAdding(true)}
          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[11px] font-bold"
          style={{ backgroundColor: `${ORANGE}12`, color: ORANGE, border: `1px solid ${ORANGE}33` }}>
          <Plus size={11} /> Add
        </button>
      </div>

      {adding && (
        <div className="mb-3">
          <ComponentSearch
            catalogIngredients={catalogIngredients}
            subRecipes={availableSubRecipes}
            existingRefs={existingRefs}
            onSelect={addComponent}
            onCancel={() => setAdding(false)}
          />
        </div>
      )}

      {local.components.length === 0 && !adding ? (
        <div className="py-8 text-center rounded-xl" style={{ border: `1.5px dashed ${NAVY}18` }}>
          {isMenuItem
            ? <UtensilsCrossed size={24} className="mx-auto mb-2" style={{ color: NAVY, opacity: 0.2 }} />
            : <ChefHat size={24} className="mx-auto mb-2" style={{ color: NAVY, opacity: 0.2 }} />}
          <p className="text-xs" style={{ color: NAVY, opacity: 0.4 }}>
            Add catalog ingredients or preps.
          </p>
        </div>
      ) : (
        <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${NAVY}10` }}>
          <table className="w-full text-xs">
            <thead>
              <tr style={{ backgroundColor: `${NAVY}03`, borderBottom: `1px solid ${NAVY}08` }}>
                {["Type", "Name", "Qty", "Unit", "Rate", "Cost", ""].map(h => (
                  <th key={h} className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-wide"
                    style={{ color: NAVY, opacity: 0.45 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {local.components.map((comp, i) => {
                let rate: number | null = null;
                let costVal: number | null = null;
                let srcUnit = comp.sourceUnit;

                if (comp.refType === "ingredient") {
                  const prod = allProducts.find(p => p.productCode === comp.refId);
                  if (prod) { rate = prod.latestUnitPrice; srcUnit = prod.unit; costVal = usageCost(comp.quantity, comp.unit, rate, srcUnit); }
                } else {
                  const sub = allRecipes.find(r => r.id === comp.refId);
                  if (sub) {
                    srcUnit = sub.yieldUnit;
                    const res = getRecipeCostPerUnit(sub, allRecipes, allProducts);
                    rate = res.value;
                    if (rate !== null) costVal = usageCost(comp.quantity, comp.unit, rate, sub.yieldUnit);
                  }
                }

                return (
                  <tr key={comp.refId}
                    style={{ borderTop: i > 0 ? `1px solid ${NAVY}07` : undefined }}
                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = `${NAVY}04`)}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = "")}>
                    <td className="px-3 py-2.5">
                      <span className="text-[9px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5"
                        style={{ backgroundColor: comp.refType === "ingredient" ? `${ORANGE}15` : `${NAVY}10`,
                          color: comp.refType === "ingredient" ? ORANGE : NAVY }}>
                        {comp.refType === "ingredient" ? "Ingredient" : "Prep"}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <p className="font-semibold truncate max-w-[140px]" style={{ color: NAVY }}>{comp.name}</p>
                    </td>
                    <td className="px-3 py-2.5">
                      <input type="number" min="0" step="0.001" value={comp.quantity}
                        onChange={e => updateComp(comp.refId, { quantity: parseFloat(e.target.value) || 0 })}
                        className="w-16 rounded-md px-2 py-1 text-xs text-center outline-none"
                        style={{ border: `1.5px solid ${NAVY}20`, color: NAVY }}
                        onFocus={e => (e.target.style.borderColor = NAVY)}
                        onBlur={e => (e.target.style.borderColor = `${NAVY}20`)} />
                    </td>
                    <td className="px-3 py-2.5">
                      <UnitSelect value={comp.unit} sourceUnit={srcUnit}
                        onChange={u => updateComp(comp.refId, { unit: u })} />
                    </td>
                    <td className="px-3 py-2.5 font-semibold" style={{ color: NAVY, opacity: 0.6 }}>
                      {rate !== null ? `$${rate.toFixed(3)}/${srcUnit}` : "—"}
                    </td>
                    <td className="px-3 py-2.5 font-bold" style={{ color: NAVY }}>{fmtCost(costVal)}</td>
                    <td className="px-3 py-2.5">
                      <button type="button" onClick={() => removeComp(comp.refId)}
                        style={{ color: UP, opacity: 0.5 }}
                        onMouseEnter={e => ((e.currentTarget as HTMLButtonElement).style.opacity = "1")}
                        onMouseLeave={e => ((e.currentTarget as HTMLButtonElement).style.opacity = "0.5")}>
                        <X size={12} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Cost history (menu items only) */}
      {isMenuItem && (
        <div className="mt-6">
          <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: NAVY, opacity: 0.55 }}>
            Cost history <span className="font-medium normal-case" style={{ color: MUTED }}>(total, by invoice date)</span>
          </p>
          <RecipePriceHistory recipe={local} allRecipes={allRecipes} allProducts={allProducts} businessId={businessId} />
        </div>
      )}
    </div>
  );
}

// ── RecipeListItem ────────────────────────────────────────────────────────────

const RecipeListItem = memo(function RecipeListItem({ recipe, selected, allRecipes, allProducts, onClick }: {
  recipe: Recipe; selected: boolean; allRecipes: Recipe[]; allProducts: AllProduct[]; onClick: () => void;
}) {
  const result = getRecipeCostPerUnit(recipe, allRecipes, allProducts);
  const isMenuItem = recipe.recipeType === "menu_item";
  const sellingPrice = recipe.sellingPrice ?? 0;
  const totalCost = result.value !== null ? result.value * recipe.yieldQty : null;
  const fcPct = totalCost !== null && sellingPrice > 0 ? (totalCost / sellingPrice) * 100 : null;
  const targetFc = recipe.targetFoodCostPct > 0 ? recipe.targetFoodCostPct : DEFAULT_FC_TARGET;

  return (
    <button data-menu-costing-list-item type="button" onClick={onClick}
      className="w-full text-left px-4 py-3 transition-colors"
      style={{ borderBottom: `1px solid ${NAVY}0a`, backgroundColor: selected ? `${NAVY}08` : "white",
        borderLeft: selected ? `3px solid ${NAVY}` : "3px solid transparent" }}
      onMouseEnter={e => { if (!selected) e.currentTarget.style.backgroundColor = `${NAVY}04`; }}
      onMouseLeave={e => { if (!selected) e.currentTarget.style.backgroundColor = SURFACE; }}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-bold truncate" style={{ color: NAVY }}>
          {recipe.name || <span style={{ opacity: 0.35 }}>Unnamed</span>}
        </p>
        {result.warning ? (
          <AlertTriangle size={12} style={{ color: "#d97706", flexShrink: 0 }} />
        ) : isMenuItem && fcPct !== null ? (
          <p className="text-[11px] font-bold flex-shrink-0" style={{ color: fcColor(fcPct, targetFc) }}>
            {fcPct.toFixed(1)}%
          </p>
        ) : result.value !== null ? (
          <p className="text-[11px] font-bold flex-shrink-0" style={{ color: ORANGE }}>
            {fmtCost(result.value)}/{recipe.yieldUnit}
          </p>
        ) : null}
      </div>
      <p className="text-[10px] mt-0.5" style={{ color: NAVY, opacity: 0.5 }}>
        {recipe.components.length} component{recipe.components.length !== 1 ? "s" : ""}
        {isMenuItem && sellingPrice > 0 ? ` · $${sellingPrice.toFixed(2)}` : ""}
        {!isMenuItem ? ` · yields ${recipe.yieldQty} ${recipe.yieldUnit}` : ""}
      </p>
    </button>
  );
});

// ── RecipeTab ─────────────────────────────────────────────────────────────────

function RecipeTab({ recipeType, recipes, allRecipes, allProducts, catalogIngredients, businessId }: {
  recipeType: "sub_recipe" | "menu_item";
  recipes: Recipe[];          // filtered to this type (for display)
  allRecipes: Recipe[];       // all recipes (for cost calculation)
  allProducts: AllProduct[];
  catalogIngredients: CatalogIngredient[];
  businessId: string | undefined;
}) {
  const queryClient = useQueryClient();
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [addingGroup, setAddingGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [virtualGroups, setVirtualGroups] = useState<string[]>([]);
  const newGroupInputRef = useRef<HTMLInputElement>(null);
  const addGroupPopoverRef = useRef<HTMLDivElement>(null);

  const singularLabel = recipeType === "sub_recipe" ? "Prep" : "Menu Item";
  const pluralLabel = recipeType === "sub_recipe" ? "Preps" : "Menu Items";

  const dataCategories = useMemo(
    () => [...new Set(recipes.map(r => r.category).filter((c): c is string => Boolean(c)))].sort(),
    [recipes],
  );
  const categories = useMemo(
    () => [...new Set([...dataCategories, ...virtualGroups.filter((g) => !dataCategories.includes(g))])].sort(),
    [dataCategories, virtualGroups],
  );
  useEffect(() => { setVirtualGroups((prev) => prev.filter((g) => !dataCategories.includes(g))); }, [dataCategories]);
  useEffect(() => { if (addingGroup) newGroupInputRef.current?.focus(); }, [addingGroup]);
  useEffect(() => {
    function onOut(e: MouseEvent) {
      if (addGroupPopoverRef.current && !addGroupPopoverRef.current.contains(e.target as Node)) {
        setAddingGroup(false);
        setNewGroupName("");
      }
    }
    if (addingGroup) document.addEventListener("mousedown", onOut);
    return () => document.removeEventListener("mousedown", onOut);
  }, [addingGroup]);

  const groupCards = useMemo(() => {
    const cards: { name: string; count: number }[] = [{ name: "All", count: recipes.length }];
    for (const cat of categories) cards.push({ name: cat, count: recipes.filter(r => r.category === cat).length });
    const ungrouped = recipes.filter(r => !r.category).length;
    if (ungrouped > 0) cards.push({ name: "Ungrouped", count: ungrouped });
    return cards;
  }, [recipes, categories]);

  function addGroup(name: string) {
    const trimmed = name.trim();
    if (!trimmed || categories.includes(trimmed)) return;
    setVirtualGroups((prev) => [...prev, trimmed]);
    setSelectedCategory(trimmed);
    setSearch("");
    setSelectedId(null);
  }

  const { mutateAsync: doCreate, isPending: creating } = useMutation({
    mutationFn: (payload: RecipePayload) => createRecipe(payload, businessId),
    onSuccess: (newRecipe) => {
      queryClient.setQueryData<Recipe[]>(["recipes", businessId], (old: Recipe[] | undefined) => [...(old ?? []), newRecipe]);
      setSelectedId(newRecipe.id);
    },
  });

  const { mutate: doUpdate } = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<RecipePayload> }) =>
      updateRecipe(id, payload, businessId),
    onSuccess: (updated) => {
      queryClient.setQueryData<Recipe[]>(["recipes", businessId],
        (old: Recipe[] | undefined) => (old ?? []).map(r => r.id === updated.id ? updated : r));
    },
  });

  const { mutate: doDelete } = useMutation({
    mutationFn: (id: string) => deleteRecipe(id, businessId),
    onSuccess: (_, id) => {
      queryClient.setQueryData<Recipe[]>(["recipes", businessId], (old: Recipe[] | undefined) => (old ?? []).filter(r => r.id !== id));
      setSelectedId(null);
    },
  });

  function handleUpdate(updated: Recipe) {
    queryClient.setQueryData<Recipe[]>(["recipes", businessId],
      (old: Recipe[] | undefined) => (old ?? []).map(r => r.id === updated.id ? updated : r));
    doUpdate({ id: updated.id, payload: recipeToPayload(updated) });
  }

  function handleConvert(updated: Recipe) {
    doUpdate({ id: updated.id, payload: { recipeType: updated.recipeType, sellingPrice: updated.sellingPrice } });
    queryClient.setQueryData<Recipe[]>(["recipes", businessId],
      (old: Recipe[] | undefined) => (old ?? []).map(r => r.id === updated.id ? updated : r));
    setSelectedId(null);
  }

  async function createNew(category?: string) {
    const defaultYieldUnit = recipeType === "menu_item" ? "serving" : "ea";
    await doCreate({
      name: "", recipeType, category: category || null,
      yieldQty: 1, yieldUnit: defaultYieldUnit,
      sellingPrice: recipeType === "menu_item" ? 0 : null,
      targetFoodCostPct: DEFAULT_FC_TARGET, notes: "", components: [],
    });
  }

  const catRecipes = selectedCategory === "All"
    ? recipes
    : selectedCategory === "Ungrouped"
    ? recipes.filter(r => !r.category)
    : recipes.filter(r => r.category === selectedCategory);

  const displayRecipes = search.trim()
    ? catRecipes.filter(r => r.name.toLowerCase().includes(search.toLowerCase()))
    : catRecipes;

  const selected = catRecipes.find(r => r.id === selectedId) ?? null;
  const defaultCategory = selectedCategory !== "All" && selectedCategory !== "Ungrouped" ? selectedCategory : undefined;

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden" style={{ background: CANVAS }}>
      <div className="flex-1 min-h-0 px-6 pb-6 pt-4 flex gap-4 overflow-hidden">
        {/* Left panel */}
        <div style={{ width: 330, flexShrink: 0, background: SURFACE, border: `1px solid ${LINE}`, borderRadius: 14, display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 1px 3px rgba(42,36,32,.08), 0 12px 28px rgba(42,36,32,.06)" }}>
          <div style={{ padding: "13px 16px", borderBottom: `1px solid ${LINE}`, background: "#fff", display: "flex", flexDirection: "column", gap: 10, flexShrink: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9, height: 36, padding: "0 13px", background: SURFACE, border: `1px solid ${LINE}`, borderRadius: 999 }}>
              <Search size={15} style={{ color: MUTED, flexShrink: 0 }} />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder={`Search ${pluralLabel.toLowerCase()}…`}
                className="outline-none bg-transparent flex-1" style={{ fontSize: 13, color: NAVY }} />
              {search && <button type="button" onClick={() => setSearch("")} style={{ color: MUTED, background: "none", border: "none", cursor: "pointer", padding: 0 }}><X size={12} /></button>}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0, position: "relative" }}>
                <select
                  value={selectedCategory}
                  onChange={(e) => { setSelectedCategory(e.target.value); setSearch(""); setSelectedId(null); }}
                  aria-label={`${pluralLabel} category`}
                  style={{ width: "100%", height: 38, borderRadius: 999, border: `1px solid ${LINE}`, background: SURFACE, color: NAVY, fontSize: 12.5, fontWeight: 700, outline: "none", padding: "0 38px 0 13px", boxShadow: "0 1px 2px rgba(80,55,30,0.05)", cursor: "pointer", appearance: "none", WebkitAppearance: "none" }}
                >
                  {groupCards.map((g) => <option key={g.name} value={g.name}>{g.name} ({g.count})</option>)}
                </select>
                <ChevronDown size={14} style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", color: MUTED, pointerEvents: "none" }} />
              </div>
              <div ref={addGroupPopoverRef} style={{ position: "relative", flexShrink: 0 }}>
                <button data-menu-costing-action type="button" onClick={() => setAddingGroup((v) => !v)} title="Add a group" aria-label="Add a group"
                  style={{ height: 38, minWidth: 112, borderRadius: 999, cursor: "pointer", background: SURFACE, border: `1px solid ${LINE}`, color: NAVY, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "0 14px", fontSize: 12, fontWeight: 800, boxShadow: "0 1px 2px rgba(80,55,30,0.05)", flexShrink: 0 }}>
                  <Plus size={13} strokeWidth={2.4} />
                  <span>Add a group</span>
                </button>
                {addingGroup && (
                  <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 30, background: SURFACE, border: `1px solid ${NAVY}14`, borderRadius: 12, padding: 12, minWidth: 200, boxShadow: "0 8px 24px rgba(80,55,30,.12)" }}>
                    <p style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", color: NAVY, opacity: 0.4, marginBottom: 8 }}>New group</p>
                    <input
                      ref={newGroupInputRef}
                      value={newGroupName}
                      onChange={(e) => setNewGroupName(e.target.value)}
                      placeholder="Group name…"
                      className="w-full outline-none"
                      style={{ border: `1.5px solid ${NAVY}24`, borderRadius: 8, padding: "6px 10px", fontSize: 12, color: NAVY, marginBottom: 8 }}
                      onFocus={(e) => (e.target.style.borderColor = ORANGE)}
                      onBlur={(e) => (e.target.style.borderColor = `${NAVY}24`)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") { const t = newGroupName.trim(); if (t) addGroup(t); setAddingGroup(false); setNewGroupName(""); }
                        if (e.key === "Escape") { setAddingGroup(false); setNewGroupName(""); }
                      }}
                    />
                    <button
                      data-menu-costing-action
                      type="button"
                      onClick={() => { const t = newGroupName.trim(); if (t) addGroup(t); setAddingGroup(false); setNewGroupName(""); }}
                      style={{ width: "100%", background: NAVY, color: "#fff", border: "none", borderRadius: 8, padding: "6px 0", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
                    >
                      Create group
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto" style={{ minHeight: 0 }}>
            {displayRecipes.length === 0 ? (
              <div style={{ padding: "48px 16px", textAlign: "center" }}>
                <ChefHat size={28} style={{ margin: "0 auto 8px", color: NAVY, opacity: 0.15 }} />
                <p style={{ fontSize: 12, color: MUTED }}>{search ? "No match." : `No ${pluralLabel.toLowerCase()} yet.`}</p>
              </div>
            ) : (
              displayRecipes.map(r => (
                <RecipeListItem key={r.id} recipe={r} selected={r.id === selectedId}
                  allRecipes={allRecipes} allProducts={allProducts} onClick={() => setSelectedId(r.id)} />
              ))
            )}
          </div>

          <div style={{ padding: 13, borderTop: `1px solid ${LINE}`, flexShrink: 0 }}>
            <button data-menu-costing-action type="button" disabled={creating} onClick={() => createNew(defaultCategory)}
              className="w-full inline-flex items-center justify-center gap-2"
              style={{ height: 36, borderRadius: 999, border: "none", background: "transparent", color: MUTED, fontSize: 12.5, fontWeight: 600, cursor: creating ? "not-allowed" : "pointer" }}
              onMouseEnter={e => { const b = e.currentTarget; b.style.color = ORANGE; }}
              onMouseLeave={e => { const b = e.currentTarget; b.style.color = MUTED; }}>
              <Plus size={13} /> New {singularLabel.toLowerCase()}
            </button>
          </div>
        </div>

        {/* Right panel */}
        <div style={{ flex: 1, minWidth: 0, background: SURFACE, border: `1px solid ${LINE}`, borderRadius: 14, overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 1px 3px rgba(42,36,32,.08), 0 12px 28px rgba(42,36,32,.06)" }}>
          {selected ? (
            <RecipeEditor
              recipe={selected}
              allRecipes={allRecipes}
              allProducts={allProducts}
              catalogIngredients={catalogIngredients}
              categories={categories}
              businessId={businessId}
              onUpdate={handleUpdate}
              onDelete={() => doDelete(selected.id)}
              onConvert={handleConvert}
            />
          ) : (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: 32 }}>
              <div style={{ width: 56, height: 56, borderRadius: 18, background: `${NAVY}07`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
                <ChefHat size={24} style={{ color: NAVY, opacity: 0.3 }} />
              </div>
              <p style={{ fontSize: 13.5, fontWeight: 700, color: NAVY, marginBottom: 4 }}>{pluralLabel}</p>
              <p style={{ fontSize: 12, color: MUTED, maxWidth: 260, lineHeight: 1.5 }}>
                Select an item to edit, or create a new {singularLabel.toLowerCase()}.
              </p>
              <button data-menu-costing-action type="button" disabled={creating} onClick={() => createNew(defaultCategory)}
                style={{ marginTop: 20, height: 38, padding: "0 20px", borderRadius: 999, background: ORANGE, color: "#fff", fontSize: 13, fontWeight: 600, border: "none", cursor: creating ? "not-allowed" : "pointer", display: "inline-flex", alignItems: "center", gap: 8, opacity: creating ? 0.5 : 1 }}>
                <Plus size={14} /> New {singularLabel.toLowerCase()}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── MenuItemsTab: design-matched master-detail ────────────────────────────────

const MC_ACCENT    = "#c25a3c";
const MC_ACCENT_WK = "rgba(194,90,60,.12)";
const MC_DOWN_C    = "#3c7d57";
const MC_DOWN_WK   = "rgba(60,125,87,.12)";
const MC_MID_C     = "#c08a2c";
const MC_MID_WK    = "rgba(192,138,44,.13)";
const MC_UP_C      = "#c2412e";
const MC_UP_WK     = "rgba(194,65,46,.12)";
const FAINT        = "#aaa093";
const RAISED       = "#fff";
const CHIP_BG      = "#f3ece1";

function mPlateCost(r: Recipe, allRecipes: Recipe[], allProd: AllProduct[]): number | null {
  const res = getRecipeCostPerUnit(r, allRecipes, allProd);
  return res.value !== null ? res.value * r.yieldQty : null;
}
function mFcPct(r: Recipe, allRecipes: Recipe[], allProd: AllProduct[]): number | null {
  const cost = mPlateCost(r, allRecipes, allProd);
  if (!cost || !r.sellingPrice || r.sellingPrice <= 0) return null;
  return (cost / r.sellingPrice) * 100;
}
function mFcCls(pct: number, target: number): "good" | "mid" | "over" {
  return pct <= target ? "good" : pct <= target + 5 ? "mid" : "over";
}
function mChipSty(cls: "good" | "mid" | "over"): React.CSSProperties {
  return cls === "good" ? { color: MC_DOWN_C, background: MC_DOWN_WK }
    : cls === "mid"  ? { color: MC_MID_C,  background: MC_MID_WK }
    : { color: MC_UP_C, background: MC_UP_WK };
}

const mcCard: React.CSSProperties = {
  background: SURFACE, border: `1px solid ${LINE}`, borderRadius: 14,
  padding: "16px 22px", display: "flex", flexDirection: "column", gap: 9,
  boxShadow: "0 1px 3px rgba(42,36,32,.08), 0 12px 28px rgba(42,36,32,.06)",
};
const mcIc: React.CSSProperties = {
  width: 28, height: 28, borderRadius: 9, background: MC_ACCENT_WK, color: MC_ACCENT,
  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
};
const mcBigN: React.CSSProperties = { fontSize: 28, fontWeight: 600, letterSpacing: "-.01em", lineHeight: 1 };
function mcPill(extra: React.CSSProperties): React.CSSProperties {
  return { display: "inline-flex", alignItems: "center", gap: 4, alignSelf: "flex-start", fontSize: 11.5, fontWeight: 600, padding: "3px 8px", borderRadius: 999, ...extra };
}

const MENU_ITEM_DRAFT_PREFIX = "__menu_item_draft__";

function isMenuItemDraftId(id: string): boolean {
  return id.startsWith(MENU_ITEM_DRAFT_PREFIX);
}

function newDraftMenuItem(businessId: string | undefined): Recipe {
  return {
    id: `${MENU_ITEM_DRAFT_PREFIX}-${crypto.randomUUID()}`,
    businessId: businessId ?? "",
    name: "",
    recipeType: "menu_item",
    category: null,
    yieldQty: 1,
    yieldUnit: "serving",
    sellingPrice: 0,
    targetFoodCostPct: DEFAULT_FC_TARGET,
    notes: "",
    createdAt: new Date().toISOString(),
    components: [],
  };
}

// ── MIListRow ─────────────────────────────────────────────────────────────────

function MIListRow({ recipe, selected, allRecipes, allProducts, onClick, isDraft }: {
  recipe: Recipe; selected: boolean; allRecipes: Recipe[]; allProducts: AllProduct[]; onClick: () => void; isDraft?: boolean;
}) {
  const cost   = mPlateCost(recipe, allRecipes, allProducts);
  const target = recipe.targetFoodCostPct > 0 ? recipe.targetFoodCostPct : DEFAULT_FC_TARGET;
  const pct    = cost !== null && recipe.sellingPrice && recipe.sellingPrice > 0 ? (cost / recipe.sellingPrice) * 100 : null;
  const cls    = pct !== null ? mFcCls(pct, target) : null;

  return (
    <button type="button" onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "9px 16px", paddingLeft: 13,
        background: selected ? MC_ACCENT_WK : "transparent",
        borderTop: "none", borderRight: "none", borderBottom: "none",
        borderLeft: `3px solid ${selected ? MC_ACCENT : "transparent"}`,
        cursor: "pointer", width: "100%", textAlign: "left", outline: "none",
      }}
      onMouseEnter={e => { if (!selected) (e.currentTarget as HTMLButtonElement).style.background = CHIP_BG; }}
      onMouseLeave={e => { if (!selected) (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}>
      <div style={{ width: 38, height: 38, borderRadius: 9, border: `1px solid ${LINE}`, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", color: FAINT, background: `repeating-linear-gradient(135deg,${CHIP_BG},${CHIP_BG} 6px,${RAISED} 6px,${RAISED} 12px)` }}>
        <UtensilsCrossed size={16} style={{ strokeWidth: 1.6 }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: NAVY, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {recipe.name || <span style={{ opacity: 0.4 }}>{isDraft ? "New menu item" : "Unnamed"}</span>}
        </div>
        <div style={{ fontSize: 11.5, color: MUTED }}>
          {isDraft ? "Unsaved draft · " : ""}{recipe.components.length} component{recipe.components.length !== 1 ? "s" : ""}
          {recipe.sellingPrice ? ` · $${recipe.sellingPrice.toFixed(2)}` : ""}
        </div>
      </div>
      {isDraft
        ? <span style={{ fontSize: 10, fontWeight: 700, color: MUTED, background: `${NAVY}08`, padding: "3px 7px", borderRadius: 999, flexShrink: 0 }}>unsaved</span>
        : cls !== null && pct !== null
        ? <span style={{ ...mChipSty(cls), fontSize: 11.5, fontWeight: 700, padding: "3px 8px", borderRadius: 999, flexShrink: 0 }}>{pct.toFixed(1)}%</span>
        : <span style={{ fontSize: 11.5, color: FAINT, flexShrink: 0 }}>—</span>}
    </button>
  );
}

// ── MIDetailView ──────────────────────────────────────────────────────────────

function MIDetailView({ recipe, allRecipes, allProducts, businessId, onEdit }: {
  recipe: Recipe; allRecipes: Recipe[]; allProducts: AllProduct[];
  businessId: string | undefined; onEdit: () => void;
}) {
  const costResult = getRecipeCostPerUnit(recipe, allRecipes, allProducts);
  const totalCost  = costResult.value !== null ? costResult.value * recipe.yieldQty : null;
  const sellPrice  = recipe.sellingPrice ?? 0;
  const targetFc   = recipe.targetFoodCostPct > 0 ? recipe.targetFoodCostPct : DEFAULT_FC_TARGET;
  const fcPct      = totalCost !== null && sellPrice > 0 ? (totalCost / sellPrice) * 100 : null;
  const margin     = totalCost !== null ? sellPrice - totalCost : null;
  const grossPct   = margin !== null && sellPrice > 0 ? (margin / sellPrice) * 100 : null;
  const cls        = fcPct !== null ? mFcCls(fcPct, targetFc) : null;
  const overBy     = fcPct !== null ? fcPct - targetFc : null;
  const SCALE      = 45;
  const fillW      = fcPct !== null ? Math.min(fcPct / SCALE * 100, 100) : 0;
  const tgtW       = Math.min(targetFc / SCALE * 100, 100);
  const barCol     = cls === "good" ? MC_DOWN_C : cls === "mid" ? MC_MID_C : MC_UP_C;

  const compDetails = recipe.components.map(comp => {
    let rate: number | null = null; let costVal: number | null = null; let srcUnit = comp.sourceUnit;
    if (comp.refType === "ingredient") {
      const prod = allProducts.find(p => p.productCode === comp.refId);
      if (prod) { rate = prod.latestUnitPrice; srcUnit = prod.unit; costVal = usageCost(comp.quantity, comp.unit, rate, srcUnit); }
    } else {
      const sub = allRecipes.find(r => r.id === comp.refId);
      if (sub) {
        srcUnit = sub.yieldUnit;
        const res = getRecipeCostPerUnit(sub, allRecipes, allProducts);
        rate = res.value;
        if (rate !== null) costVal = usageCost(comp.quantity, comp.unit, rate, sub.yieldUnit);
      }
    }
    return { ...comp, rate, costVal, srcUnit };
  });

  const kLbl: React.CSSProperties = { fontSize: 11, color: MUTED, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em" };
  const kVal: React.CSSProperties = { fontSize: 23, fontWeight: 600, letterSpacing: "-.01em", lineHeight: 1, fontVariantNumeric: "tabular-nums" };
  const thS: React.CSSProperties = { textAlign: "left", padding: "9px 22px", fontSize: 10.5, letterSpacing: ".03em", textTransform: "uppercase", color: FAINT, fontWeight: 600, borderBottom: `1px solid ${LINE}`, whiteSpace: "nowrap" };
  const tdS: React.CSSProperties = { padding: "0 22px", height: 46, borderBottom: `1px solid ${LINE}`, verticalAlign: "middle" };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Dish header */}
      <div style={{ display: "flex", gap: 18, padding: 22, borderBottom: `1px solid ${LINE}`, flexShrink: 0 }}>
        <div style={{ width: 128, height: 128, flexShrink: 0, borderRadius: 14, border: `1px solid ${LINE}`, display: "flex", alignItems: "center", justifyContent: "center", color: FAINT, background: `repeating-linear-gradient(135deg,${CHIP_BG},${CHIP_BG} 8px,${RAISED} 8px,${RAISED} 16px)` }}>
          <UtensilsCrossed size={36} style={{ strokeWidth: 1.4 }} />
        </div>
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 11.5, color: MUTED, fontWeight: 600, marginBottom: 5 }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: MC_ACCENT, flexShrink: 0 }} />
            {recipe.category || "Uncategorised"} · Menu item
          </div>
          <h2 style={{ margin: 0, fontSize: 25, fontWeight: 600, letterSpacing: "-.01em", lineHeight: 1.1, color: NAVY }}>{recipe.name || "Unnamed item"}</h2>
          <div style={{ marginTop: 12 }}>
            <button data-menu-costing-action type="button" onClick={onEdit}
              style={{ height: 38, padding: "0 20px", borderRadius: 999, border: `1.5px solid ${MC_ACCENT}`, background: MC_ACCENT, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8, boxShadow: "0 2px 8px rgba(194,90,60,.22)" }}>
              <Pencil size={14} style={{ strokeWidth: 2.2 }} />Edit
            </button>
          </div>
        </div>
      </div>

      {/* Scrollable body */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        {/* KPI strip */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)" }}>
          {[
            { label: "Plate cost",   val: totalCost !== null ? `$${totalCost.toFixed(2)}` : "—",            sub: `${recipe.components.length} items`,            subCol: MUTED,   valCol: NAVY },
            { label: "Menu price",   val: sellPrice > 0 ? `$${sellPrice.toFixed(2)}` : "—",                 sub: sellPrice > 0 ? "on menu" : "not set",           subCol: MUTED,   valCol: NAVY },
            { label: "Food cost",    val: fcPct !== null ? `${fcPct.toFixed(1)}%` : "—",
              sub: overBy !== null ? `${Math.abs(overBy).toFixed(1)} pts ${overBy > 0 ? "over" : "under"} · tgt ${targetFc}%` : `target ${targetFc}%`,
              subCol: overBy !== null && overBy > 0 ? MC_UP_C : MC_DOWN_C,
              valCol: cls === "good" ? MC_DOWN_C : cls === "mid" ? MC_MID_C : cls === "over" ? MC_UP_C : NAVY },
            { label: "Gross profit", val: margin !== null && sellPrice > 0 ? `$${margin.toFixed(2)}` : "—", sub: grossPct !== null ? `${grossPct.toFixed(1)}% margin` : "", subCol: MUTED, valCol: NAVY },
          ].map((k, i) => (
            <div key={k.label} style={{ padding: "16px 22px", borderRight: i < 3 ? `1px solid ${LINE}` : "none", borderBottom: `1px solid ${LINE}`, display: "flex", flexDirection: "column", gap: 7 }}>
              <span style={kLbl}>{k.label}</span>
              <span style={{ ...kVal, color: k.valCol }}>{k.val}</span>
              <span style={{ fontSize: 11.5, fontWeight: 500, color: k.subCol }}>{k.sub}</span>
            </div>
          ))}
        </div>
        {/* Recipe breakdown */}
        <div style={{ padding: "15px 22px 11px", display: "flex", alignItems: "center", gap: 10 }}>
          <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "#564d43" }}>Recipe breakdown</h3>
          <span style={{ fontSize: 11, color: MUTED, border: `1px solid ${LINE}`, borderRadius: 999, padding: "3px 9px", fontWeight: 500 }}>{recipe.components.length} components</span>
        </div>

        {recipe.components.length === 0 ? (
          <div style={{ padding: 32, textAlign: "center", color: MUTED, fontSize: 12.5 }}>
            No components yet.
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr>
                <th style={thS}>Ingredient</th>
                <th style={{ ...thS, textAlign: "right" }}>Qty</th>
                <th style={{ ...thS, textAlign: "right" }}>Unit cost</th>
                <th style={{ ...thS, textAlign: "right" }}>Line cost</th>
                <th style={{ ...thS, textAlign: "right" }}>% of plate</th>
              </tr>
            </thead>
            <tbody>
              {compDetails.map(comp => {
                const share = comp.costVal !== null && totalCost ? (comp.costVal / totalCost) * 100 : null;
                return (
                  <tr key={comp.refId}
                    onMouseEnter={e => (e.currentTarget.style.background = `${MC_ACCENT_WK}55`)}
                    onMouseLeave={e => (e.currentTarget.style.background = "")}>
                    <td style={tdS}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: NAVY }}>{comp.name}</span>
                      {comp.refType === "recipe" && <span style={{ marginLeft: 6, fontSize: 10, color: FAINT, letterSpacing: ".04em" }}>PREP</span>}
                    </td>
                    <td style={{ ...tdS, textAlign: "right" }}>
                      <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{comp.quantity}<span style={{ color: MUTED, fontWeight: 500, fontSize: 11.5, marginLeft: 2 }}>{comp.unit}</span></span>
                    </td>
                    <td style={{ ...tdS, textAlign: "right" }}>
                      <span style={{ fontVariantNumeric: "tabular-nums", color: MUTED, fontSize: 12 }}>{comp.rate !== null ? `$${comp.rate.toFixed(3)}/${comp.srcUnit}` : "—"}</span>
                    </td>
                    <td style={{ ...tdS, textAlign: "right" }}>
                      <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{comp.costVal !== null ? `$${comp.costVal.toFixed(2)}` : "—"}</span>
                    </td>
                    <td style={{ ...tdS, textAlign: "right", fontVariantNumeric: "tabular-nums", fontSize: 12, color: MUTED }}>
                      {share !== null ? `${share.toFixed(0)}%` : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td style={{ padding: "13px 22px", fontWeight: 700, fontSize: 12, textTransform: "uppercase" as const, letterSpacing: ".04em", color: "#564d43" }}>Total plate cost</td>
                <td /><td />
                <td style={{ padding: "13px 22px", fontWeight: 700, fontSize: 15, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{totalCost !== null ? `$${totalCost.toFixed(2)}` : "—"}</td>
                <td style={{ padding: "13px 22px", fontWeight: 600, fontSize: 15, textAlign: "right", color: MUTED, fontVariantNumeric: "tabular-nums" }}>100%</td>
              </tr>
            </tfoot>
          </table>
        )}

        {/* Plate cost history */}
        <div style={{ padding: "18px 22px 22px", display: "flex", flexDirection: "column", gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase" as const, letterSpacing: ".04em", color: MUTED }}>Plate cost · history</span>
          <RecipePriceHistory recipe={recipe} allRecipes={allRecipes} allProducts={allProducts} businessId={businessId} />
        </div>
      </div>
    </div>
  );
}

// ── MenuItemsTab ──────────────────────────────────────────────────────────────

function MenuItemsTab({ menuItems, allRecipes, allProducts, catalogIngredients, businessId }: {
  menuItems: Recipe[];
  allRecipes: Recipe[];
  allProducts: AllProduct[];
  catalogIngredients: CatalogIngredient[];
  businessId: string | undefined;
}) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<"cat" | "fc" | "margin" | "name">("cat");
  const [selectedId, setSelectedId] = useState<string | null>(() => menuItems[0]?.id ?? null);
  const [editing, setEditing] = useState(false);
  const [draftMenuItem, setDraftMenuItem] = useState<Recipe | null>(null);

  useEffect(() => { if (!selectedId && menuItems.length > 0) setSelectedId(menuItems[0].id); }, [menuItems, selectedId]);
  useEffect(() => {
    if (!draftMenuItem || selectedId !== draftMenuItem.id) setEditing(false);
  }, [selectedId, draftMenuItem?.id]);

  const { mutateAsync: doCreate, isPending: creating } = useMutation({
    mutationFn: (payload: RecipePayload) => createRecipe(payload, businessId),
    onSuccess: (newRecipe) => {
      queryClient.setQueryData<Recipe[]>(["recipes", businessId], (old: Recipe[] | undefined) => [...(old ?? []), newRecipe]);
    },
  });
  const { mutate: doUpdate } = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<RecipePayload> }) => updateRecipe(id, payload, businessId),
    onSuccess: (updated) => { queryClient.setQueryData<Recipe[]>(["recipes", businessId], (old: Recipe[] | undefined) => (old ?? []).map(r => r.id === updated.id ? updated : r)); },
  });
  const { mutate: doDelete } = useMutation({
    mutationFn: (id: string) => deleteRecipe(id, businessId),
    onSuccess: (_, id) => {
      queryClient.setQueryData<Recipe[]>(["recipes", businessId], (old: Recipe[] | undefined) => (old ?? []).filter(r => r.id !== id));
      setSelectedId(menuItems.find(m => m.id !== id)?.id ?? null);
    },
  });

  function handleUpdate(updated: Recipe) {
    if (isMenuItemDraftId(updated.id)) {
      setDraftMenuItem((prev) => prev?.id === updated.id ? updated : prev);
      return;
    }
    queryClient.setQueryData<Recipe[]>(["recipes", businessId], (old: Recipe[] | undefined) => (old ?? []).map(r => r.id === updated.id ? updated : r));
    doUpdate({ id: updated.id, payload: recipeToPayload(updated) });
  }
  function handleConvert(updated: Recipe) {
    if (isMenuItemDraftId(updated.id)) {
      setDraftMenuItem((prev) => prev?.id === updated.id ? updated : prev);
      return;
    }
    doUpdate({ id: updated.id, payload: { recipeType: updated.recipeType, sellingPrice: updated.sellingPrice } });
    queryClient.setQueryData<Recipe[]>(["recipes", businessId], (old: Recipe[] | undefined) => (old ?? []).map(r => r.id === updated.id ? updated : r));
    setSelectedId(null);
  }
  function startDraft() {
    const draft = newDraftMenuItem(businessId);
    setDraftMenuItem(draft);
    setSelectedId(draft.id);
    setEditing(true);
  }
  function cancelDraft() {
    setDraftMenuItem(null);
    setSelectedId(menuItems[0]?.id ?? null);
    setEditing(false);
  }
  async function saveDraft(draft: Recipe) {
    if (creating) return;
    try {
      const normalized: Recipe = {
        ...draft,
        name: draft.name.trim() || "New menu item",
        recipeType: "menu_item",
        yieldQty: draft.yieldQty > 0 ? draft.yieldQty : 1,
        yieldUnit: draft.yieldUnit || "serving",
        sellingPrice: draft.sellingPrice ?? 0,
        targetFoodCostPct: draft.targetFoodCostPct > 0 ? draft.targetFoodCostPct : DEFAULT_FC_TARGET,
      };
      const created = await doCreate(recipeToPayload(normalized));
      setDraftMenuItem(null);
      setSelectedId(created.id);
      setEditing(false);
    } catch (e) {
      console.error("Failed to save menu item:", e);
    }
  }

  // Filtered + sorted list
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    let rows = q ? menuItems.filter(m => m.name.toLowerCase().includes(q)) : [...menuItems];
    if (sortBy === "fc")     rows.sort((a, b) => (mFcPct(b, allRecipes, allProducts) ?? -1) - (mFcPct(a, allRecipes, allProducts) ?? -1));
    else if (sortBy === "margin") rows.sort((a, b) => {
      const ma = mPlateCost(a, allRecipes, allProducts) !== null && a.sellingPrice ? a.sellingPrice - mPlateCost(a, allRecipes, allProducts)! : 9999;
      const mb = mPlateCost(b, allRecipes, allProducts) !== null && b.sellingPrice ? b.sellingPrice - mPlateCost(b, allRecipes, allProducts)! : 9999;
      return ma - mb;
    });
    else if (sortBy === "name") rows.sort((a, b) => a.name.localeCompare(b.name));
    return rows;
  }, [menuItems, search, sortBy, allRecipes, allProducts]);

  const grouped = useMemo<Map<string, Recipe[]> | null>(() => {
    if (sortBy !== "cat") return null;
    const m = new Map<string, Recipe[]>();
    for (const r of filtered) { const k = r.category || "Uncategorised"; if (!m.has(k)) m.set(k, []); m.get(k)!.push(r); }
    return m;
  }, [filtered, sortBy]);

  const isDraftSelected = Boolean(draftMenuItem && selectedId === draftMenuItem.id);
  const selected   = isDraftSelected ? draftMenuItem : menuItems.find(m => m.id === selectedId) ?? null;
  const categories = useMemo(() => [...new Set(menuItems.map(m => m.category).filter(Boolean) as string[])].sort(), [menuItems]);

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden" style={{ background: CANVAS }}>
      {/* Master-detail */}
      <div className="flex-1 min-h-0 px-6 pb-6 pt-4 flex gap-4 overflow-hidden">
        {/* Left panel */}
        <div style={{ width: 330, flexShrink: 0, background: SURFACE, border: `1px solid ${LINE}`, borderRadius: 14, display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 1px 3px rgba(42,36,32,.08), 0 12px 28px rgba(42,36,32,.06)" }}>
          <div style={{ padding: "13px 16px", borderBottom: `1px solid ${LINE}`, background: RAISED, display: "flex", flexDirection: "column", gap: 11, flexShrink: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9, height: 36, padding: "0 13px", background: SURFACE, border: `1px solid ${LINE}`, borderRadius: 999 }}>
              <Search size={15} style={{ color: MUTED, flexShrink: 0 }} />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search menu items…"
                className="outline-none bg-transparent flex-1" style={{ fontSize: 13, color: NAVY }} />
              {search && <button type="button" onClick={() => setSearch("")} style={{ color: MUTED, background: "none", border: "none", cursor: "pointer", padding: 0 }}><X size={12} /></button>}
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 11, color: MUTED, fontWeight: 500 }}>{filtered.length} dish{filtered.length !== 1 ? "es" : ""}</span>
              <select value={sortBy} onChange={e => setSortBy(e.target.value as typeof sortBy)} className="outline-none"
                style={{ appearance: "none" as const, border: `1px solid ${LINE}`, background: SURFACE, borderRadius: 999, padding: "5px 28px 5px 12px", fontSize: 11.5, fontWeight: 600, color: NAVY, cursor: "pointer",
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%238a8074' stroke-width='2.5'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center" }}>
                <option value="cat">Group · Category</option>
                <option value="fc">Sort · Highest food cost</option>
                <option value="margin">Sort · Lowest margin</option>
                <option value="name">Sort · Name A–Z</option>
              </select>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto" style={{ minHeight: 0 }}>
            {menuItems.length === 0 && !draftMenuItem ? (
              <div style={{ padding: "48px 16px", textAlign: "center" }}>
                <UtensilsCrossed size={28} style={{ margin: "0 auto 8px", color: NAVY, opacity: 0.15 }} />
                <p style={{ fontSize: 13, color: MUTED, marginBottom: 0 }}>No menu items yet.</p>
              </div>
            ) : (
              <>
                {draftMenuItem && (
                  <MIListRow
                    recipe={draftMenuItem}
                    selected={draftMenuItem.id === selectedId}
                    allRecipes={allRecipes}
                    allProducts={allProducts}
                    isDraft
                    onClick={() => { setSelectedId(draftMenuItem.id); setEditing(true); }}
                  />
                )}
                {grouped ? (
                  [...grouped.entries()].map(([cat, rows]) => (
                    <div key={cat}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "13px 16px 6px" }}>
                        <span style={{ fontSize: 10, letterSpacing: ".12em", textTransform: "uppercase" as const, color: FAINT, fontWeight: 500 }}>{cat}</span>
                        <div style={{ flex: 1, height: 1, background: LINE }} />
                      </div>
                      {rows.map(m => <MIListRow key={m.id} recipe={m} selected={m.id === selectedId} allRecipes={allRecipes} allProducts={allProducts} onClick={() => { setDraftMenuItem(null); setSelectedId(m.id); }} />)}
                    </div>
                  ))
                ) : (
                  filtered.map(m => <MIListRow key={m.id} recipe={m} selected={m.id === selectedId} allRecipes={allRecipes} allProducts={allProducts} onClick={() => { setDraftMenuItem(null); setSelectedId(m.id); }} />)
                )}
              </>
            )}
          </div>

          <div style={{ padding: 13, borderTop: `1px solid ${LINE}`, flexShrink: 0 }}>
            <button type="button" onClick={startDraft} disabled={creating}
              className="w-full inline-flex items-center justify-center gap-2"
              style={{ height: 36, borderRadius: 999, border: "none", background: "transparent", color: MUTED, fontSize: 12.5, fontWeight: 600, cursor: creating ? "not-allowed" : "pointer" }}
              onMouseEnter={e => { const b = e.currentTarget; b.style.color = MC_ACCENT; }}
              onMouseLeave={e => { const b = e.currentTarget; b.style.color = MUTED; }}>
              <Plus size={13} />New menu item
            </button>
          </div>
        </div>

        {/* Right panel */}
        <div style={{ flex: 1, minWidth: 0, background: SURFACE, border: `1px solid ${LINE}`, borderRadius: 14, overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 1px 3px rgba(42,36,32,.08), 0 12px 28px rgba(42,36,32,.06)" }}>
          {!selected ? (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 32, textAlign: "center" }}>
              <div style={{ width: 56, height: 56, borderRadius: 14, background: `${NAVY}07`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
                <UtensilsCrossed size={24} style={{ color: NAVY, opacity: 0.3 }} />
              </div>
              <p style={{ fontSize: 14, fontWeight: 700, color: NAVY, marginBottom: 6 }}>Select a menu item</p>
              <p style={{ fontSize: 12.5, color: MUTED, maxWidth: 280, lineHeight: 1.5, marginBottom: 20 }}>Choose a dish from the list to see its food cost breakdown and plate margin.</p>
              <button data-menu-costing-action type="button" onClick={startDraft} disabled={creating}
                style={{ height: 38, padding: "0 20px", borderRadius: 999, border: `1px solid ${MC_ACCENT}`, background: MC_ACCENT, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 }}>
                <Plus size={15} />New menu item
              </button>
            </div>
          ) : editing ? (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <div style={{ padding: "10px 16px", borderBottom: `1px solid ${LINE}`, display: "flex", alignItems: "center", gap: 10, flexShrink: 0, background: RAISED }}>
                {isDraftSelected ? (
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: MC_ACCENT }}>Unsaved menu item</span>
                ) : (
                  <>
                    <button type="button" onClick={() => setEditing(false)}
                      style={{ fontSize: 12.5, fontWeight: 600, color: MC_ACCENT, background: "transparent", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                      ← Back to details
                    </button>
                    <span style={{ fontSize: 12, color: MUTED }}>·</span>
                    <span style={{ fontSize: 12, color: MUTED }}>{selected.name || "Unnamed item"}</span>
                  </>
                )}
              </div>
              <div style={{ flex: 1, overflow: "auto" }}>
                <RecipeEditor
                  recipe={selected} allRecipes={allRecipes} allProducts={allProducts}
                  catalogIngredients={catalogIngredients} categories={categories}
                  businessId={businessId} onUpdate={handleUpdate}
                  onDelete={() => { doDelete(selected.id); setEditing(false); }}
                  onConvert={handleConvert}
                  isDraft={isDraftSelected}
                  saveDraftPending={creating}
                  onSaveDraft={saveDraft}
                  onCancelDraft={cancelDraft}
                />
              </div>
            </div>
          ) : (
            <MIDetailView
              recipe={selected} allRecipes={allRecipes} allProducts={allProducts}
              businessId={businessId} onEdit={() => setEditing(true)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function MenuCostingPage() {
  const { activeBusiness } = useBusiness();
  const [pageTab, setPageTab] = useState<"ingredient" | "sub_recipe" | "menu_item">("ingredient");

  const { data: invoicesData } = useQuery<Invoice[]>({
    queryKey: ["invoices", activeBusiness?.id],
    queryFn: () => fetchProcessedInvoices(activeBusiness?.id),
    enabled: Boolean(activeBusiness),
  });
  const invoices: Invoice[] = invoicesData ?? [];

  const { data: catalogIngredientsData } = useQuery<CatalogIngredient[]>({
    queryKey: ["ingredients", activeBusiness?.id],
    queryFn: () => fetchIngredients(activeBusiness?.id),
    enabled: Boolean(activeBusiness),
  });
  const catalogIngredients: CatalogIngredient[] = catalogIngredientsData ?? [];

  const { data: recipesData } = useQuery<Recipe[]>({
    queryKey: ["recipes", activeBusiness?.id],
    queryFn: () => fetchRecipes(activeBusiness?.id),
    enabled: Boolean(activeBusiness),
  });
  const recipes: Recipe[] = recipesData ?? [];

  const allProducts = useMemo<AllProduct[]>(() => {
    const productMap = new Map<string, AllProduct>();
    for (const inv of invoices) {
      const seenAt = invoiceActivityDate(inv);
      for (const item of inv.lineItems) {
        const code = item.productCode ?? `${inv.supplier.id}-UNKNOWN`;
        const existing = productMap.get(code);
        if (existing) {
          existing.totalSpend += item.totalPrice ?? 0;
          existing.timesOrdered += 1;
          if (seenAt >= existing.latestSeenAt) {
            existing.latestSeenAt = seenAt;
            existing.latestUnitPrice = item.unitPrice ?? existing.latestUnitPrice;
            existing.unit = item.unit ?? existing.unit;
            existing.description = item.description ?? existing.description;
            existing.supplierId = inv.supplier.id;
            existing.supplierName = inv.supplier.name;
          }
        } else {
          productMap.set(code, {
            productCode: code, description: item.description ?? "Unknown",
            supplierId: inv.supplier.id, supplierName: inv.supplier.name,
            unit: item.unit ?? "ea", latestUnitPrice: item.unitPrice ?? 0,
            latestSeenAt: seenAt, totalSpend: item.totalPrice ?? 0, timesOrdered: 1,
          });
        }
      }
    }
    const invoiceProducts = [...productMap.values()].sort((a, b) => a.description.localeCompare(b.description));
    const catalogProducts: AllProduct[] = catalogIngredients.map(ing => ({
      productCode: ing.id, description: ing.name, supplierId: ing.currentSupplierId ?? "",
      supplierName: "", unit: ing.canonicalUnit, latestUnitPrice: ing.currentPrice ?? 0,
      latestSeenAt: ing.sources.find(s => s.isPreferred)?.latestDate ?? ing.sources[0]?.latestDate ?? new Date().toISOString().slice(0, 10),
      totalSpend: 0, timesOrdered: ing.sourceCount, isIngredient: true, sourceCount: ing.sourceCount,
    }));
    return [...catalogProducts, ...invoiceProducts];
  }, [invoices, catalogIngredients]);

  const allRecipes = recipes;
  const subRecipes = allRecipes.filter(r => r.recipeType === "sub_recipe");
  const menuItems  = allRecipes.filter(r => r.recipeType === "menu_item");

  const navItems = [
    { key: "ingredient" as const, label: "Base Ingredients", count: catalogIngredients.length },
    { key: "sub_recipe" as const,  label: "Preps",           count: subRecipes.length },
    { key: "menu_item" as const,   label: "Menu Items",      count: menuItems.length },
  ];

  return (
    <div className="menu-costing-page flex flex-col h-full"
      style={{ backgroundColor: CANVAS, fontFamily: "var(--font-ui)" }}>
      <div className="flex-shrink-0 pl-9 pr-6 pt-9 pb-0"
        style={{ backgroundColor: CANVAS }}>
        <div>
          <h1 className="text-[27px] font-semibold leading-tight" style={{ color: NAVY }}>Menu Costing</h1>
          <p className="text-sm mt-0.5" style={{ color: MUTED }}>
            Track ingredient costs, prep recipes, and menu item margins.
          </p>
        </div>
        <div className="mt-7 overflow-x-auto rounded-[22px]"
          style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}`, boxShadow: "0 1px 2px rgba(80,55,30,0.05)" }}>
          <nav className="grid min-w-[360px] grid-cols-3 xl:min-w-0" aria-label="Menu costing sections">
            {navItems.map(({ key, label, count }) => {
              const active = pageTab === key;
              return (
                <button data-menu-costing-nav key={key} type="button" onClick={() => setPageTab(key)}
                  className="relative flex h-10 items-center justify-center gap-2 px-3 text-xs font-semibold transition-colors"
                  style={{ color: active ? MC_ACCENT : NAVY, opacity: active ? 1 : 0.7 }}>
                  {label}
                  {active
                    ? <span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: MC_ACCENT_WK, color: MC_ACCENT }}>{count}</span>
                    : <span className="text-[10px] font-bold" style={{ color: MUTED, opacity: 0.65 }}>{count}</span>
                  }
                  <span className="absolute bottom-0 left-6 right-6 h-[2px] rounded-t-full transition-opacity"
                    style={{ backgroundColor: MC_ACCENT, opacity: active ? 1 : 0 }} />
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        {pageTab === "ingredient" && <BaseIngredientTab />}
        {pageTab === "sub_recipe" && (
          <RecipeTab
            recipeType="sub_recipe"
            recipes={subRecipes}
            allRecipes={allRecipes}
            allProducts={allProducts}
            catalogIngredients={catalogIngredients}
            businessId={activeBusiness?.id}
          />
        )}
        {pageTab === "menu_item" && (
          <MenuItemsTab
            menuItems={menuItems}
            allRecipes={allRecipes}
            allProducts={allProducts}
            catalogIngredients={catalogIngredients}
            businessId={activeBusiness?.id}
          />
        )}
      </div>
    </div>
  );
}
